import React, { useState, useEffect, useRef, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import toast from 'react-hot-toast';
import { MousePointer2, LayoutGrid, CalendarDays, Users, Images, BookImage, SlidersHorizontal, Upload, Download } from 'lucide-react';
import { photosApi } from '../../api/photos';
import { denyAction, isPermissionError } from '../../store/permissionDeniedStore';
import { overlaysApi } from '../../api/overlays';
import { ftpAccountsApi } from '../../api/ftpAccounts';
import { exportConfigsApi } from '../../api/exportConfigs';
import { brandingApi } from '../../api/settings';
import { Event, Photo, Overlay, FTPAccount, ExportConfig, OverlayConfig } from '../../types';
import { PhotoGrid } from './PhotoGrid';
import { PhotoCard } from './PhotoCard';
import { EditorModal } from '../Editor/EditorModal';
import { PhotoViewerModal } from './PhotoViewerModal';
import { UploadModal } from './UploadModal';
import { UploadingCards } from './UploadingCards';
import { GalleryFiltersPanel, type GalleryFilters } from './GalleryFiltersPanel';
import { NewPhotosBanner } from './NewPhotosBanner';
import { BatchActionBar } from './BatchActionBar';
import { AddToAlbumSheet } from '../Albums/AddToAlbumSheet';
import { AlbumManagementPanel } from '../Albums/AlbumManagementPanel';
import { ClaySwitch } from '../UI/ClaySwitch';
import { LivePollIndicator } from '../UI/LivePollIndicator';
import { PhotoCardSkeleton } from '../UI/Skeleton';
import { EmptyState } from '../UI/EmptyState';
import { TabBar, type TabItem } from '../UI/TabBar';
import { MachhaCharacter } from '../../lib/machhaAsset';
import { track, EVENTS } from '../../lib/mixpanel';
import { subscribeToEventPhotos } from '../../api/sse';
import { useEditorStore } from '../../store/editorStore';
import { useUploadStore } from '../../store/uploadStore';
import { clayStaggerContainer, clayStaggerItem } from '../../constants/animations';
import { EXPORT_TARGETS } from '../../constants/exportTargets';
import { buildSourceCanvas, renderToTarget, CENTER_REFRAME } from '../../lib/exportRender';
import { CLIENT_ZIP_ENABLED, startClientZip } from '../../services/download';
import styles from './GalleryPanel.module.css';

const POLL_INTERVAL = 5000;

type GroupBy = 'none' | 'date' | 'photographer';

const GROUP_BY_OPTIONS: { value: GroupBy; label: string; icon: React.ElementType }[] = [
  { value: 'none',         label: 'None',         icon: LayoutGrid   },
  { value: 'date',         label: 'Date',         icon: CalendarDays },
  { value: 'photographer', label: 'Photographer', icon: Users        },
];

// ── Sub-tabs ──────────────────────────────────────────────────────────────────

type GallerySubTab = 'photos' | 'albums';

const PHOTOS_TAB: TabItem<GallerySubTab> = { id: 'photos', label: 'Photos', icon: Images };
const ALBUMS_TAB: TabItem<GallerySubTab> = { id: 'albums', label: 'Albums', icon: BookImage };

const MANUAL_UPLOAD_KEY = '_manual_';

/**
 * Apply the gallery's client-side filters (search / photographer / date) and
 * sort. Shared by the rendered list and "Select all" so both agree on which
 * photos are in scope.
 */
function filterAndSortPhotos(
  photos: Photo[],
  filters: GalleryFilters,
  photographerById: Record<string, string>,
): Photo[] {
  let list = [...photos];

  if (filters.search) {
    const q = filters.search.toLowerCase();
    list = list.filter(
      (p) =>
        p.originalFilename.toLowerCase().includes(q) ||
        (photographerById[p.ftpAccountId ?? MANUAL_UPLOAD_KEY] ?? '').toLowerCase().includes(q)
    );
  }

  if (filters.photographerId !== 'all') {
    if (filters.photographerId === MANUAL_UPLOAD_KEY) {
      list = list.filter((p) => !p.ftpAccountId);
    } else {
      list = list.filter((p) => p.ftpAccountId === filters.photographerId);
    }
  }

  if (filters.fromDate) {
    const from = new Date(filters.fromDate);
    list = list.filter((p) => new Date(p.uploadedAt) >= from);
  }

  if (filters.toDate) {
    const to = new Date(filters.toDate);
    to.setHours(23, 59, 59, 999);
    list = list.filter((p) => new Date(p.uploadedAt) <= to);
  }

  list.sort((a, b) => {
    const aTime = new Date(a.uploadedAt).getTime();
    const bTime = new Date(b.uploadedAt).getTime();
    return filters.sort === 'asc' ? aTime - bTime : bTime - aTime;
  });

  return list;
}

// ── Props ─────────────────────────────────────────────────────────────────────

interface GalleryPanelProps {
  eventId: string;
  eventStatus: 'active' | 'ended';
  event: Event;
  isCreator: boolean;
  canCreateAlbum?: boolean;
  /** Whether the current user may delete photos. Managers without the
   *  `allowDeletingOfPhotos` permission see a "no access" modal instead. */
  canDeletePhotos?: boolean;
}

// ── Component ─────────────────────────────────────────────────────────────────

export const GalleryPanel: React.FC<GalleryPanelProps> = ({ eventId, eventStatus, event, isCreator, canCreateAlbum, canDeletePhotos = true }) => {
  const editorOpen = useEditorStore((s) => s.isOpen);
  const [subTab, setSubTab] = useState<GallerySubTab>('photos');
  // Albums tab visible to all event team members — visibility is enforced by the backend.
  const subTabs = useMemo(() => [PHOTOS_TAB, ALBUMS_TAB], []);

  const [photos, setPhotos] = useState<Photo[]>([]);
  const [overlays, setOverlays] = useState<Overlay[]>([]);
  const [ftpAccounts, setFtpAccounts] = useState<FTPAccount[]>([]);
  const [exportConfigs, setExportConfigs] = useState<ExportConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [totalPhotoCount, setTotalPhotoCount] = useState<number | null>(null);
  const [processingCount, setProcessingCount] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  const [newPhotoCount, setNewPhotoCount] = useState(0);
  const [autoSync, setAutoSync] = useState(true);
  const [selectMode, setSelectMode] = useState(false);
  const [downloadingAll, setDownloadingAll] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [selectingAll, setSelectingAll] = useState(false);
  const [batchDeleting, setBatchDeleting] = useState(false);
  const [showAddToAlbum, setShowAddToAlbum] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [filters, setFilters] = useState<GalleryFilters>({
    search: '',
    photographerId: 'all',
    fromDate: '',
    toDate: '',
    sort: 'desc',
  });
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const catchUpTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const latestUploadedAt = useRef<string | null>(null);
  const autoSyncRef = useRef(autoSync);
  const sseActiveRef = useRef(false); // true while SSE is connected; polling backs off when true
  const lastSelectedIndexRef = useRef<number>(-1);
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const setSelectionActive = useUploadStore((s) => s.setSelectionActive);
  // Stable boolean — flips once when an upload for this event starts/clears, so
  // GalleryPanel doesn't re-render on per-file status churn or progress ticks.
  const hasActiveUploads = useUploadStore((s) => s.tasks.some((t) => t.eventId === eventId));
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerIndex, setViewerIndex] = useState(0);
  const [defaultEditorOverlay, setDefaultEditorOverlay] = useState<OverlayConfig | null>(null);
  const [groupBy, setGroupBy] = useState<GroupBy>(() => {
    try { return (localStorage.getItem('gallery-group-by') as GroupBy) || 'none'; }
    catch { return 'none'; }
  });

  const handleGroupByChange = (g: GroupBy) => {
    setGroupBy(g);
    try { localStorage.setItem('gallery-group-by', g); } catch { /* ignore */ }
  };

  useEffect(() => { autoSyncRef.current = autoSync; }, [autoSync]);

  // Infinite scroll sentinel
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && hasMore && !loadingMore) loadMorePhotos();
      },
      { rootMargin: '200px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasMore, loadingMore]); // eslint-disable-line react-hooks/exhaustive-deps

  // Open upload modal from UploadPill or other callers
  useEffect(() => {
    const handler = () => setUploadModalOpen(true);
    window.addEventListener('upload:open-modal', handler);
    return () => window.removeEventListener('upload:open-modal', handler);
  }, []);

  // Reflect background uploads (from SW or another tab) into processingCount
  useEffect(() => {
    const handler = (e: CustomEvent<{ eventId?: string }>) => {
      if (e.detail?.eventId !== eventId) return;
      setProcessingCount((c) => c + 1);
    };
    window.addEventListener('upload:photo-ready', handler as EventListener);
    return () => window.removeEventListener('upload:photo-ready', handler as EventListener);
  }, [eventId]);

  // Sync selection state to uploadStore so UploadPill can shift above BatchActionBar
  useEffect(() => {
    setSelectionActive(selectMode);
    return () => { if (!selectMode) setSelectionActive(false); };
  }, [selectMode, setSelectionActive]);

  // Initial load
  useEffect(() => {
    if (eventId) loadInitialData();
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      if (catchUpTimerRef.current) clearTimeout(catchUpTimerRef.current);
      setSelectionActive(false);
    };
  }, [eventId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Polling
  useEffect(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    if (eventStatus === 'active' && eventId) {
      startPolling();
    }
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [eventStatus, eventId]); // eslint-disable-line react-hooks/exhaustive-deps

  // SSE — real-time photo updates (active events only)
  useEffect(() => {
    if (eventStatus !== 'active' || !eventId) return;
    return subscribeToEventPhotos(eventId, {
      onConnected: () => { sseActiveRef.current = true; },
      onDisconnected: () => { sseActiveRef.current = false; },
      onPhotoCreated: (photo) => {
        latestUploadedAt.current = photo.uploadedAt;
        setProcessingCount((c) => Math.max(0, c - 1));
        setTotalPhotoCount((c) => (c ?? 0) + 1);
        if (autoSyncRef.current) {
          setPhotos((prev) => prev.some((p) => p._id === photo._id) ? prev : [photo, ...prev]);
        } else {
          setPhotos((prev) => {
            if (prev.some((p) => p._id === photo._id)) return prev;
            return prev; // don't prepend when auto-sync is off
          });
          setNewPhotoCount((c) => c + 1);
        }
      },
      // The original finished compressing — clear the cell's "preparing" state
      // and swap in the now-openable original URL so opening no longer fails.
      onPhotoReady: ({ photoId, originalUrl }) => {
        setPhotos((prev) =>
          prev.map((p) =>
            p._id === photoId
              ? { ...p, status: 'ready', originalUrl: originalUrl || p.originalUrl }
              : p,
          ),
        );
      },
    });
  }, [eventStatus, eventId]);

  // Keyboard shortcuts for select mode
  const handleBatchDeleteRef = useRef<() => void>(() => {});

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      // Ignore when typing in an input / textarea / contenteditable
      const tag = (e.target as HTMLElement).tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || (e.target as HTMLElement).isContentEditable) return;

      // Escape — exit select mode
      if (e.key === 'Escape' && selectMode) {
        exitSelectMode();
        return;
      }

      // Cmd/Ctrl+A — select all visible photos
      if ((e.metaKey || e.ctrlKey) && e.key === 'a' && subTab === 'photos') {
        e.preventDefault();
        if (!selectMode) setSelectMode(true);
        handleSelectAll();
        return;
      }

      // Delete / Backspace — batch delete when in select mode with selection
      if ((e.key === 'Delete' || e.key === 'Backspace') && selectMode && selectedIds.size > 0) {
        handleBatchDeleteRef.current();
      }
    };

    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selectMode, selectedIds, subTab]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadInitialData = async () => {
    try {
      setLoading(true);
      const [photosData, overlaysData, ftpData, configsData, brandingDefaults] = await Promise.all([
        photosApi.list(eventId),
        overlaysApi.list(eventId),
        ftpAccountsApi.list(eventId),
        exportConfigsApi.list(eventId),
        brandingApi.getDefaults().catch(() => null),
      ]);
      setPhotos(photosData.photos);
      setNextCursor(photosData.nextCursor);
      setHasMore(photosData.hasMore);
      if (photosData.totalCount !== undefined) setTotalPhotoCount(photosData.totalCount);
      if (photosData.processingCount !== undefined) setProcessingCount(photosData.processingCount);
      setOverlays(overlaysData);

      // Pre-resolve the default branding overlay so the editor opens with it applied.
      if (brandingDefaults) {
        const matched = overlaysData.find((o) => o.storagePath === brandingDefaults.storagePath);
        if (matched) {
          setDefaultEditorOverlay({
            overlayId: matched._id,
            anchor: brandingDefaults.config.anchor,
            size: brandingDefaults.config.size,
            opacity: brandingDefaults.config.opacity,
          });
        }
      }
      setFtpAccounts(ftpData);
      setExportConfigs(configsData);
      latestUploadedAt.current = photosData.photos.length > 0
        ? photosData.photos[0].uploadedAt
        : new Date().toISOString();
      track(EVENTS.GALLERY_VIEWED, {
        event_id: eventId,
        event_name: event.name,
        event_status: eventStatus,
        photo_count: photosData.photos.length,
        has_more: photosData.hasMore,
      });
    } catch {
      setError('Failed to load gallery');
      toast.error('Failed to load gallery');
    } finally {
      setLoading(false);
    }
  };

  const resetFilters = () =>
    setFilters({ search: '', photographerId: 'all', fromDate: '', toDate: '', sort: 'desc' });

  const photographerById = useMemo(() => {
    const m: Record<string, string> = {};
    ftpAccounts.forEach((a) => { m[a._id] = a.photographerName || a.username; });
    m[MANUAL_UPLOAD_KEY] = 'Manual Upload';
    return m;
  }, [ftpAccounts]);

  const filteredPhotos = useMemo(
    () => filterAndSortPhotos(photos, filters, photographerById),
    [photos, filters, photographerById],
  );

  const globalIndexByPhotoId = useMemo(() => {
    const m: Record<string, number> = {};
    filteredPhotos.forEach((p, i) => { m[p._id] = i; });
    return m;
  }, [filteredPhotos]);

  // Photo ids already in the gallery — an uploading tile drops once its
  // processed photo lands here (matched by id from the upload task).
  const landedPhotoIds = useMemo(() => new Set(photos.map((p) => p._id)), [photos]);

  type PhotoGroup = { key: string; label: string; sublabel: string; photos: Photo[] };

  const groupedPhotos = useMemo((): PhotoGroup[] | null => {
    if (groupBy === 'none') return null;

    const todayYMD = new Date().toISOString().slice(0, 10);
    const yestYMD  = new Date(Date.now() - 86_400_000).toISOString().slice(0, 10);

    const buckets = new Map<string, Photo[]>();
    for (const photo of filteredPhotos) {
      const key = groupBy === 'date'
        ? photo.uploadedAt.slice(0, 10)
        : (photo.ftpAccountId ?? MANUAL_UPLOAD_KEY);
      if (!buckets.has(key)) buckets.set(key, []);
      buckets.get(key)!.push(photo);
    }

    return Array.from(buckets.entries()).map(([key, photos]) => {
      let label: string;
      if (groupBy === 'date') {
        if (key === todayYMD) label = 'Today';
        else if (key === yestYMD) label = 'Yesterday';
        else label = new Date(key + 'T00:00:00').toLocaleDateString('en-US', {
          weekday: 'long', month: 'short', day: 'numeric',
        });
      } else {
        label = photographerById[key] || 'Unknown photographer';
      }
      return { key, label, sublabel: `${photos.length} photo${photos.length !== 1 ? 's' : ''}`, photos };
    });
  }, [filteredPhotos, groupBy, photographerById]);

  const openViewerAt = (index: number) => {
    setViewerIndex(index);
    setViewerOpen(true);
    track(EVENTS.PHOTO_VIEWED, { event_id: eventId, photo_index: index });
  };

  const startPolling = () => {
    pollRef.current = setInterval(async () => {
      // Back off — SSE is delivering photos in real-time
      if (sseActiveRef.current) return;
      if (!latestUploadedAt.current) return;
      try {
        const data = await photosApi.poll(eventId, latestUploadedAt.current);
        // Reconcile to the backend's authoritative counts rather than blindly
        // incrementing — the same photo can also arrive via SSE or the
        // post-upload catch-up poll, and double-counting inflated the total.
        if (data.totalCount !== undefined) setTotalPhotoCount(data.totalCount);
        if (data.processingCount !== undefined) setProcessingCount(data.processingCount);
        if (data.photos.length === 0) return;
        latestUploadedAt.current = data.photos[0].uploadedAt;
        if (autoSyncRef.current) {
          setPhotos((prev) => {
            const existing = new Set(prev.map((p) => p._id));
            const fresh = data.photos.filter((p) => !existing.has(p._id));
            return fresh.length > 0 ? [...fresh, ...prev] : prev;
          });
          setNewPhotoCount(0);
        } else {
          setNewPhotoCount((c) => c + data.photos.length);
        }
      } catch { /* Ignore transient poll errors */ }
    }, POLL_INTERVAL);
  };

  const loadMorePhotos = async () => {
    if (!nextCursor || loadingMore) return;
    track(EVENTS.PHOTOS_LOAD_MORE, { event_id: eventId, photos_loaded_so_far: photos.length });
    setLoadingMore(true);
    try {
      const data = await photosApi.list(eventId, nextCursor);
      setPhotos((prev) => [...prev, ...data.photos]);
      setNextCursor(data.nextCursor);
      setHasMore(data.hasMore);
    } catch {
      toast.error('Failed to load more photos');
    } finally {
      setLoadingMore(false);
    }
  };

  const handleRefresh = async () => {
    track(EVENTS.GALLERY_REFRESHED, { event_id: eventId, new_photo_count: newPhotoCount });
    setNewPhotoCount(0);
    const data = await photosApi.list(eventId);
    setPhotos(data.photos);
    setNextCursor(data.nextCursor);
    setHasMore(data.hasMore);
    if (data.totalCount !== undefined) setTotalPhotoCount(data.totalCount);
    if (data.processingCount !== undefined) setProcessingCount(data.processingCount);
    if (data.photos.length > 0) latestUploadedAt.current = data.photos[0].uploadedAt;
  };

  const handlePhotoDeleted = (photoId: string) => {
    setPhotos((prev) => prev.filter((p) => p._id !== photoId));
    setTotalPhotoCount((c) => (c ?? 1) - 1);
    track(EVENTS.PHOTO_DELETED, { event_id: eventId, photo_id: photoId });
    toast.success('Photo deleted');
  };

  const handlePhotoHighlightChanged = (photoId: string, isHighlight: boolean) => {
    setPhotos((prev) => prev.map((p) => (p._id === photoId ? { ...p, isHighlight } : p)));
  };

  // ── Selection handlers ────────────────────────────────────────────────────

  const toggleSelect = (id: string, shiftKey = false) => {
    const idx = filteredPhotos.findIndex((p) => p._id === id);
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (shiftKey && lastSelectedIndexRef.current >= 0 && idx >= 0) {
        // Range select: toggle all items between last selected and current
        const from = Math.min(lastSelectedIndexRef.current, idx);
        const to = Math.max(lastSelectedIndexRef.current, idx);
        const shouldSelect = !prev.has(id);
        for (let i = from; i <= to; i++) {
          if (shouldSelect) next.add(filteredPhotos[i]._id);
          else next.delete(filteredPhotos[i]._id);
        }
      } else {
        if (next.has(id)) next.delete(id);
        else next.add(id);
      }
      if (next.size === 0) setSelectMode(false);
      return next;
    });
    if (idx >= 0) lastSelectedIndexRef.current = idx;
  };

  // Loads every remaining page into `photos` and returns the full list. Used
  // when an action needs the actual photo objects for the whole selection
  // (filtered "Select all", or client-side export) rather than just their ids.
  const loadAllPhotos = async (): Promise<Photo[]> => {
    if (!hasMore || !nextCursor) return photos;
    const collected = [...photos];
    let cursor: string | null = nextCursor;
    while (cursor) {
      const data = await photosApi.list(eventId, cursor);
      collected.push(...data.photos);
      cursor = data.nextCursor;
    }
    setPhotos(collected);
    setNextCursor(null);
    setHasMore(false);
    return collected;
  };

  const handleSelectAll = async () => {
    // Filters that change *which* photos are in scope (sort doesn't). When none
    // are active we grab every id from the server in one request — no need to
    // load (and sign URLs for) thousands of full photo objects. With a filter
    // active we need the objects to evaluate it client-side.
    const filtersNarrowSet =
      filters.search !== '' ||
      filters.photographerId !== 'all' ||
      filters.fromDate !== '' ||
      filters.toDate !== '';

    setSelectingAll(true);
    try {
      if (!filtersNarrowSet) {
        setSelectedIds(new Set(await photosApi.listIds(eventId)));
      } else {
        const all = await loadAllPhotos();
        setSelectedIds(
          new Set(filterAndSortPhotos(all, filters, photographerById).map((p) => p._id)),
        );
      }
    } catch {
      toast.error('Failed to select all photos');
    } finally {
      setSelectingAll(false);
    }
  };
  const handleDeselectAll = () => setSelectedIds(new Set());

  const exitSelectMode = () => {
    setSelectMode(false);
    setSelectedIds(new Set());
  };

  const handleEnterSelectMode = (id: string) => {
    setSelectMode(true);
    setSelectedIds(new Set([id]));
  };

  const handleBatchDelete = async () => {
    // Managers without delete permission get a clear "no access" modal up front.
    if (!canDeletePhotos) {
      denyAction('delete photos');
      return;
    }
    if (!confirm(`Delete ${selectedIds.size} photo${selectedIds.size !== 1 ? 's' : ''}? This cannot be undone.`)) return;
    setBatchDeleting(true);
    const ids = Array.from(selectedIds);
    try {
      // The delete endpoint caps each request at 200 ids, and "Select all" can
      // now select far more — send them in batches.
      const DELETE_BATCH = 200;
      for (let i = 0; i < ids.length; i += DELETE_BATCH) {
        await photosApi.batchDelete(eventId, ids.slice(i, i + DELETE_BATCH));
      }
      setPhotos((prev) => prev.filter((p) => !selectedIds.has(p._id)));
      setTotalPhotoCount((c) => (c ?? ids.length) - ids.length);
      ids.forEach((id) => track(EVENTS.PHOTO_DELETED, { event_id: eventId, photo_id: id }));
      setSelectedIds(new Set());
      toast.success(`${ids.length} photo${ids.length !== 1 ? 's' : ''} deleted`);
    } catch (err) {
      // Defense in depth: if the server rejects on permission (e.g. access was
      // revoked after load), show the same access modal instead of a vague toast.
      if (isPermissionError(err)) {
        denyAction('delete photos');
      } else {
        toast.error('Some photos could not be deleted');
      }
    } finally {
      setBatchDeleting(false);
    }
  };

  // Keep ref in sync so the keyboard handler always calls the latest version
  handleBatchDeleteRef.current = handleBatchDelete;

  const handleBatchHighlight = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    const idSet = new Set(ids);
    // Optimistic: mark all selected as highlighted immediately.
    setPhotos((prev) => prev.map((p) => (idSet.has(p._id) ? { ...p, isHighlight: true } : p)));
    try {
      await photosApi.batchSetHighlight(eventId, ids, true);
      toast.success(`${ids.length} photo${ids.length !== 1 ? 's' : ''} highlighted`);
      exitSelectMode();
    } catch {
      // revert on failure
      setPhotos((prev) => prev.map((p) => (idSet.has(p._id) ? { ...p, isHighlight: false } : p)));
      toast.error('Could not highlight photos');
    }
  };

  /**
   * Download the whole event gallery as a ZIP, built in the browser. Same
   * engine the album view uses — the only difference is the target, since these
   * photos belong to an event rather than an album.
   */
  const handleDownloadAll = async () => {
    if (downloadingAll) return;
    setDownloadingAll(true);
    try {
      await startClientZip({
        target: { kind: 'event', id: eventId },
        albumName: event?.name ?? 'photos',
        scope: 'all',
      });
    } catch (err) {
      console.error('[gallery] download all failed:', err);
      toast.error('Download failed');
    } finally {
      setDownloadingAll(false);
    }
  };

  /** Download just the selected photos. "Select all" ids are sent as-is. */
  const handleDownloadSelected = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0 || downloadingAll) return;
    setDownloadingAll(true);
    try {
      await startClientZip({
        target: { kind: 'event', id: eventId },
        albumName: event?.name ?? 'photos',
        scope: 'selection',
        photoIds: ids,
      });
    } catch (err) {
      console.error('[gallery] download selected failed:', err);
      toast.error('Download failed');
    } finally {
      setDownloadingAll(false);
    }
  };

  const handleBatchExport = async (config: ExportConfig) => {
    const ids = Array.from(selectedIds);
    // "Select all" may have selected ids whose photo objects aren't loaded
    // (ids-only fast path). Client-side export zips each original in the
    // browser, so it needs the objects — pull in any that are missing.
    const loadedIds = new Set(photos.map((p) => p._id));
    const source = ids.some((id) => !loadedIds.has(id)) ? await loadAllPhotos() : photos;
    const idSet = new Set(ids);
    const selectedPhotos = source.filter((p) => idSet.has(p._id));
    const toastId = 'batch-export';
    toast.loading(`Preparing ${ids.length} photo${ids.length !== 1 ? 's' : ''} for export…`, { id: toastId });
    try {
      const JSZip = (await import('jszip')).default;
      const zip = new JSZip();
      const folder = zip.folder(`${config.name.replace(/\s+/g, '-').toLowerCase()}-export`);
      const targets = EXPORT_TARGETS.filter((t) => config.selectedFormats.includes(`${t.platform}-${t.label}`));

      if (targets.length === 0) {
        // No formats selected on this preset ("Original Photos" or a
        // filters/overlay-only preset with nothing to resize to) — ship the
        // untouched originals, same as before.
        await Promise.all(
          selectedPhotos.map(async (photo) => {
            if (!photo.originalUrl) return;
            const resp = await fetch(photo.originalUrl);
            const blob = await resp.blob();
            // Stored bytes are always JPEG — normalize ZIP entry name to .jpg
            // so .arw / .hif source filenames don't end up as broken-looking files.
            const jpgName = photo.originalFilename.replace(/\.[^.]+$/, '') + '.jpg';
            folder!.file(jpgName, blob);
          })
        );
      } else {
        // Real preset: bake in filters + overlay(s) and resize per selected
        // platform format — mirrors the single-photo editor's export pipeline.
        await Promise.all(
          selectedPhotos.map(async (photo) => {
            const sourceCanvas = await buildSourceCanvas(photosApi.originalUrl(photo._id), config.filters);
            const baseName = photo.originalFilename.replace(/\.[^.]+$/, '');
            await Promise.all(
              targets.map(async (target) => {
                const blob = await renderToTarget(
                  target,
                  sourceCanvas,
                  config.overlay,
                  overlays,
                  CENTER_REFRAME,
                  defaultEditorOverlay,
                );
                folder!.file(`${baseName}-${target.platform}-${target.label}-${target.width}x${target.height}.jpg`, blob);
              })
            );
          })
        );
      }
      const content = await zip.generateAsync({ type: 'blob' });
      const url = URL.createObjectURL(content);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${config.name.replace(/\s+/g, '-').toLowerCase()}-export.zip`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`${ids.length} photo${ids.length !== 1 ? 's' : ''} exported!`, { id: toastId });
    } catch (err) {
      console.error('Batch export error:', err);
      toast.error('Export failed', { id: toastId });
    }
  };

  // ── Loading state ─────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className={styles['loading']}>
        <motion.p
          className={styles['loading-text']}
          animate={{ opacity: [0.4, 1, 0.4] }}
          transition={{ repeat: Infinity, duration: 2, ease: 'easeInOut' }}
        >
          Loading gallery…
        </motion.p>
        <motion.div
          className={styles['loading-character']}
          initial={{ y: 60, opacity: 0 }}
          animate={{ y: [8, -4, 8], opacity: 1 }}
          transition={{ y: { repeat: Infinity, duration: 2.5, ease: 'easeInOut' }, opacity: { duration: 0.5 } }}
        >
          <MachhaCharacter state="Loading" size={180} />
        </motion.div>
      </div>
    );
  }

  // ── Error state ───────────────────────────────────────────────────────────

  if (error) {
    return (
      <div className={styles['error']}>
        <div className={styles['error-content']}>
          <p className={styles['error-message']}>{error}</p>
        </div>
        <motion.div
          className={styles['error-character']}
          initial={{ x: 40, opacity: 0, rotate: -10 }}
          animate={{ x: 0, opacity: 1, rotate: [-8, -12, -8] }}
          transition={{ rotate: { repeat: Infinity, duration: 3, ease: 'easeInOut' }, default: { duration: 0.5 } }}
        >
          <MachhaCharacter state="Error" size={160} />
        </motion.div>
      </div>
    );
  }

  // ── Render ────────────────────────────────────────────────────────────────

  // Count active filters for badge
  const activeFilterCount = [
    filters.search !== '',
    filters.photographerId !== 'all',
    filters.fromDate !== '',
    filters.toDate !== '',
    filters.sort !== 'desc',
  ].filter(Boolean).length;

  return (
    <>
      {/* Combined row: Sub-tabs (left) + toolbar controls (right) */}
      <div className={styles['toolbar']}>
        <div className={styles['toolbar-left']}>
          {subTabs.length > 1 && (
            <TabBar
              tabs={subTabs}
              activeTab={subTab}
              onChange={setSubTab}
              variant="underline"
              layoutId="gallery-sub-tab"
            />
          )}
          {subTab === 'photos' && totalPhotoCount !== null && (
            <span className={styles['photo-count']}>
              {filteredPhotos.length < photos.length
                ? `${filteredPhotos.length} of ${totalPhotoCount} photo${totalPhotoCount !== 1 ? 's' : ''}`
                : `${totalPhotoCount} photo${totalPhotoCount !== 1 ? 's' : ''}`}
            </span>
          )}
          {subTab === 'photos' && processingCount > 0 && (
            <span className={styles['preparing-badge']}>
              <span className={styles['preparing-dot']} />
              {processingCount} preparing
            </span>
          )}
        </div>

        {subTab === 'photos' && (
          <div className={styles['toolbar-right']}>
            {/* Auto sync toggle */}
            {eventStatus === 'active' && (
              <div className={styles['auto-sync']}>
                <LivePollIndicator />
                <ClaySwitch
                  id="auto-sync"
                  checked={autoSync}
                  onChange={setAutoSync}
                  label="Auto sync"
                />
              </div>
            )}

            {/* Manual upload — desktop only */}
            {isCreator && (
              <motion.button
                onClick={() => setUploadModalOpen(true)}
                className={[styles['upload-button'], styles['upload-button-idle']].join(' ')}
                whileTap={{ scale: 0.95 }}
                transition={{ type: 'spring', stiffness: 500, damping: 25 }}
              >
                <Upload className={styles['icon-xs']} />
                Upload
              </motion.button>
            )}

            {/* Download the whole gallery — client-side archiver, progress in
                the DownloadPanel. Hidden in select mode, where the batch bar
                offers "Download" for the selection instead. */}
            {!selectMode && CLIENT_ZIP_ENABLED && filteredPhotos.length > 0 && (
              <motion.button
                onClick={handleDownloadAll}
                disabled={downloadingAll}
                className={[styles['pill-button'], styles['pill-inactive']].join(' ')}
                whileTap={{ scale: 0.95 }}
                transition={{ type: 'spring', stiffness: 500, damping: 25 }}
              >
                <Download className={styles['icon-xs']} />
                {downloadingAll ? 'Preparing…' : 'Download all'}
              </motion.button>
            )}

            {/* Select mode toggle */}
            <motion.button
              onClick={() => (selectMode ? exitSelectMode() : setSelectMode(true))}
              className={[styles['pill-button'], selectMode ? styles['pill-active-indigo'] : styles['pill-inactive']].join(' ')}
              whileTap={{ scale: 0.95 }}
              transition={{ type: 'spring', stiffness: 500, damping: 25 }}
            >
              <MousePointer2 className={styles['icon-xs']} />
              {selectMode ? 'Cancel' : 'Select'}
            </motion.button>

            {/* Filters toggle */}
            <motion.button
              onClick={() => setFiltersOpen((o) => !o)}
              className={[styles['pill-button'], filtersOpen || activeFilterCount > 0 ? styles['pill-active-orange'] : styles['pill-inactive']].join(' ')}
              whileTap={{ scale: 0.95 }}
              transition={{ type: 'spring', stiffness: 500, damping: 25 }}
            >
              <SlidersHorizontal className={styles['icon-xs']} />
              Filters
              {activeFilterCount > 0 && (
                <span className={styles['filter-badge']}>
                  {activeFilterCount}
                </span>
              )}
            </motion.button>

            {/* Group by toggle with animated pill */}
            <div className={styles['group-switcher']}>
              {GROUP_BY_OPTIONS.map(({ value, label, icon: Icon }) => (
                <motion.button
                  key={value}
                  onClick={() => handleGroupByChange(value)}
                  className={[styles['group-option'], groupBy === value ? styles['group-option-active'] : ''].filter(Boolean).join(' ')}
                  whileTap={{ scale: 0.93 }}
                  transition={{ type: 'spring', stiffness: 500, damping: 25 }}
                >
                  {groupBy === value && (
                    <motion.span
                      layoutId="group-by-pill"
                      className={styles['group-pill']}
                      transition={{ type: 'spring', stiffness: 500, damping: 35 }}
                    />
                  )}
                  <Icon className={styles['icon-xs']} />
                  <span className={styles['group-label']}>{label}</span>
                </motion.button>
              ))}
            </div>
          </div>
        )}
      </div>

      {subTab === 'photos' ? (
        <>
          {/* Collapsible filter bar + new-photos banner */}
          <AnimatePresence initial={false}>
            {filtersOpen && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ type: 'spring', stiffness: 340, damping: 32 }}
                className={styles['overflow-hidden']}
              >
                <div className={styles['filters-wrapper']}>
                  <GalleryFiltersPanel
                    filters={filters}
                    ftpAccounts={ftpAccounts}
                    hasManualUploads={photos.some((p) => !p.ftpAccountId)}
                    onChange={(newFilters) => {
                      if (newFilters.photographerId !== filters.photographerId) {
                        track(EVENTS.GALLERY_FILTERED, { event_id: eventId, filter_type: 'photographer', value: newFilters.photographerId });
                      }
                      if (newFilters.sort !== filters.sort) {
                        track(EVENTS.GALLERY_FILTERED, { event_id: eventId, filter_type: 'sort', value: newFilters.sort });
                      }
                      setFilters(newFilters);
                    }}
                    onReset={resetFilters}
                  />
                </div>
              </motion.div>
            )}
          </AnimatePresence>
          <div className={styles['banner-row']}>
            <NewPhotosBanner count={newPhotoCount} onClick={handleRefresh} />
          </div>

          {/* Live uploading tiles — full-bleed, sits directly above the
              processed photos. Renders nothing when no uploads are in flight. */}
          {isCreator && (
            <div className={styles['photo-grid-breakout']}>
              <UploadingCards eventId={eventId} landedPhotoIds={landedPhotoIds} />
            </div>
          )}

          {/* Photo content — breakout div cancels page container side padding
              so the grid spans edge-to-edge (matches AlbumViewPage behaviour).
              Empty state is exempt (it self-centers and doesn't need full bleed). */}
          <div className={!loading && photos.length === 0 && !hasActiveUploads ? undefined : styles['photo-grid-breakout']}>
            {loading && photos.length === 0 ? (
              <motion.div
                className={styles['skeleton-grid']}
                variants={clayStaggerContainer}
                initial="hidden"
                animate="visible"
              >
                {Array.from({ length: 18 }).map((_, i) => (
                  <motion.div key={i} variants={clayStaggerItem}>
                    <PhotoCardSkeleton />
                  </motion.div>
                ))}
              </motion.div>
            ) : !loading && photos.length === 0 && !hasActiveUploads ? (
              <EmptyState
                characterState="Empty"
                characterSize={240}
                title="No photos yet"
                description="Photos will appear here as photographers upload via FTP."
              />
            ) : groupedPhotos ? (
              <div>
                {groupedPhotos.map((group) => (
                  <motion.section
                    key={group.key}
                    className={styles['group-section']}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.25 }}
                  >
                    <div className={[styles['group-header'], styles['group-header-padded']].join(' ')}>
                      <h2 className={styles['group-title']}>
                        {group.label}
                      </h2>
                      <span className={styles['group-count']}>
                        {group.sublabel}
                      </span>
                      <div className={styles['group-divider']} />
                    </div>
                    <div className={styles['group-grid']}>
                      {group.photos.map((photo) => (
                        <PhotoCard
                          key={photo._id}
                          photo={photo}
                          overlays={overlays}
                          onDelete={handlePhotoDeleted}
                          canDelete={canDeletePhotos}
                          onHighlightChange={handlePhotoHighlightChanged}
                          onOpen={() => openViewerAt(globalIndexByPhotoId[photo._id])}
                          selectMode={selectMode}
                          selected={selectedIds.has(photo._id)}
                          onToggleSelect={toggleSelect}
                          onEnterSelectMode={handleEnterSelectMode}
                          defaultOverlayConfig={defaultEditorOverlay}
                          compact
                        />
                      ))}
                    </div>
                  </motion.section>
                ))}
                <div ref={sentinelRef} className={styles['sentinel']} />
                {loadingMore && (
                  <div className={styles['load-more-spinner']}>
                    <div className={styles['spinner-dot']} />
                  </div>
                )}
              </div>
            ) : (
              <PhotoGrid
                photos={filteredPhotos}
                overlays={overlays}
                hasMore={hasMore}
                onLoadMore={loadMorePhotos}
                loadingMore={loadingMore}
                onPhotoDeleted={handlePhotoDeleted}
                canDeletePhotos={canDeletePhotos}
                onHighlightChange={handlePhotoHighlightChanged}
                onOpenPhoto={(_, idx) => openViewerAt(idx)}
                selectMode={selectMode}
                selectedIds={selectedIds}
                onToggleSelect={toggleSelect}
                onEnterSelectMode={handleEnterSelectMode}
                defaultOverlayConfig={defaultEditorOverlay}
              />
            )}
          </div>

          <EditorModal />
          <UploadModal
            isOpen={uploadModalOpen}
            onClose={() => setUploadModalOpen(false)}
            eventId={eventId}
            onUploaded={(newPhotos) => {
              // Capture the pre-upload cursor so the catch-up poll can find the new photos
              // even if SSE was interrupted by the browser's HTTP connection limit during upload.
              const sinceBeforeUpload = latestUploadedAt.current;

              // Photos return with status:'processing' — the compressor Lambda hasn't
              // run yet so there's no real thumbnail. Skip prepending them here;
              // the SSE 'photo.created' event from the webhook adds them when ready.
              const readyPhotos = newPhotos.filter((p) => !p.status || p.status === 'ready');
              setPhotos((prev) => {
                const existingIds = new Set(prev.map((p) => p._id));
                const newOnly = readyPhotos.filter((p) => !existingIds.has(p._id));
                return newOnly.length > 0 ? [...newOnly, ...prev] : prev;
              });
              // Don't bump the total here — fresh uploads are still 'processing'
              // and the backend's total excludes those. Show them as "preparing"
              // instead; the catch-up poll (and SSE/regular poll) reconcile the
              // total to the authoritative count once compression finishes.
              setProcessingCount((c) => c + newPhotos.length);
              if (newPhotos.length > 0) latestUploadedAt.current = newPhotos[0].uploadedAt;

              // Catch-up poll — fires regardless of SSE state after Lambda processing time.
              // Recovers photos that SSE missed because the connection was interrupted during upload.
              if (catchUpTimerRef.current) clearTimeout(catchUpTimerRef.current);
              catchUpTimerRef.current = setTimeout(async () => {
                if (!sinceBeforeUpload) return;
                try {
                  const data = await photosApi.poll(eventId, sinceBeforeUpload);
                  // Reconcile to authoritative counts: the upload handler and
                  // SSE may have already tallied these photos, so adding
                  // data.photos.length here double-counted them.
                  if (data.totalCount !== undefined) setTotalPhotoCount(data.totalCount);
                  if (data.processingCount !== undefined) setProcessingCount(data.processingCount);
                  if (data.photos.length === 0) return;
                  latestUploadedAt.current = data.photos[0].uploadedAt;
                  setPhotos((prev) => {
                    const existing = new Set(prev.map((p) => p._id));
                    const fresh = data.photos.filter((p) => !existing.has(p._id));
                    return fresh.length > 0 ? [...fresh, ...prev] : prev;
                  });
                } catch { /* ignore — SSE is the primary path */ }
              }, 12_000);
            }}
          />
          <PhotoViewerModal
            isOpen={viewerOpen}
            onClose={() => setViewerOpen(false)}
            photos={filteredPhotos}
            initialIndex={viewerIndex}
            photographerById={photographerById}
            overlays={overlays}
            onHighlightChange={handlePhotoHighlightChanged}
            defaultOverlayConfig={defaultEditorOverlay}
          />

          <AnimatePresence>
            {selectMode && (
              <BatchActionBar
                selectedCount={selectedIds.size}
                totalCount={filteredPhotos.length}
                exportConfigs={exportConfigs}
                onDelete={handleBatchDelete}
                onExportWithPreset={handleBatchExport}
                onDownload={CLIENT_ZIP_ENABLED ? handleDownloadSelected : undefined}
                downloading={downloadingAll}
                onSelectAll={handleSelectAll}
                onDeselectAll={handleDeselectAll}
                selectingAll={selectingAll}
                onExit={exitSelectMode}
                deleting={batchDeleting}
                onAddToAlbum={isCreator ? () => setShowAddToAlbum(true) : undefined}
                onHighlight={isCreator ? handleBatchHighlight : undefined}
              />
            )}
          </AnimatePresence>

          <AddToAlbumSheet
            isOpen={showAddToAlbum}
            onClose={() => setShowAddToAlbum(false)}
            eventId={eventId}
            photoIds={[...selectedIds]}
            onAdded={() => setShowAddToAlbum(false)}
          />
        </>
      ) : (
        /* Albums sub-tab */
        <div className={styles['subtab-padded']}>
          <AlbumManagementPanel
            eventId={eventId}
            eventName={event.name}
            eventStatus={eventStatus}
            isEventCreator={isCreator}
            canCreateAlbum={Boolean(canCreateAlbum)}
          />
        </div>
      )}

      {/* Mobile floating upload button — glassmorphism (hidden when editor is open or in select mode) */}
      {isCreator && subTab === 'photos' && !editorOpen && !selectMode && (
        <motion.button
          onClick={() => setUploadModalOpen(true)}
          className={[styles['mobile-upload'], styles['mobile-upload-idle']].join(' ')}
          initial={{ y: 80, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ type: 'spring', stiffness: 400, damping: 28, delay: 0.2 }}
        >
          <Upload className={styles['icon-md']} />
          Upload Photos
        </motion.button>
      )}
    </>
  );
};
